import { useLazyListAccountsQuery } from 'api/payments/payments';
import { PAYMENT_TYPES } from 'appConstants';
import { predefinedRangeOptions } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import {
  defaultSearchParams,
  luxonDateSearchParam,
  querySearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'util/multi-select';

export const usePaymentTypeLabels = () => {
  const { t } = useTranslation();

  const TypeLabels = {
    [PAYMENT_TYPES.ALL]: t('page.transactions.paymentTypes.all.label'),
    [PAYMENT_TYPES.ORDER]: t('page.transactions.paymentTypes.orders.label'),
    [PAYMENT_TYPES.GIFTCARD_PURCHASE]: t(
      'page.transactions.paymentTypes.giftCardPurchase.label',
    ),
    [PAYMENT_TYPES.GIFTCARD_ADD_FUNDS]: t(
      'page.transactions.paymentTypes.giftCardReloads.label',
    ),
    [PAYMENT_TYPES.SCAN_TO_PAY]: t(
      'page.transactions.paymentTypes.scanToPayOrders.label',
    ),
  };

  return TypeLabels;
};

export const useTransactionSearchParams = () => {
  const { t } = useTranslation();
  const TypeLabels = usePaymentTypeLabels();

  const typeOptions: Option[] = useMemo(
    () => [
      {
        label: TypeLabels.ALL,
        id: PAYMENT_TYPES.ALL,
      },
      {
        label: TypeLabels.ORDER,
        id: PAYMENT_TYPES.ORDER,
      },
      {
        label: TypeLabels.GIFTCARD_PURCHASE,
        id: PAYMENT_TYPES.GIFTCARD_PURCHASE,
      },
      {
        label: TypeLabels.GIFTCARD_ADD_FUNDS,
        id: PAYMENT_TYPES.GIFTCARD_ADD_FUNDS,
      },
      {
        label: TypeLabels.SCAN_TO_PAY,
        id: PAYMENT_TYPES.SCAN_TO_PAY,
      },
    ],
    [
      TypeLabels.ALL,
      TypeLabels.GIFTCARD_ADD_FUNDS,
      TypeLabels.GIFTCARD_PURCHASE,
      TypeLabels.ORDER,
      TypeLabels.SCAN_TO_PAY,
    ],
  );

  const statusOptions = useMemo(
    () => [
      {
        label: t('page.transactions.filters.statusFilter.all.label'),
        id: 'ALL',
      },
      {
        label: t('page.transactions.filters.statusFilter.successful.label'),
        id: 'SUCCEEDED',
      },
      {
        label: t('page.transactions.filters.statusFilter.refunded.label'),
        id: 'REFUNDED',
      },
      {
        label: t(
          'page.transactions.filters.statusFilter.partiallyRefunded.label',
        ),
        id: 'PARTIALLY_REFUNDED',
      },
      {
        label: t('page.transactions.filters.statusFilter.refundPending.label'),
        id: 'REFUND_PENDING',
      },
      {
        label: t('page.transactions.filters.statusFilter.refundFailed.label'),
        id: 'REFUND_FAILED',
      },
      {
        label: t('page.transactions.filters.statusFilter.paymentFailed.label'),
        id: 'FAILED',
      },
    ],
    [t],
  );

  const [triggerAccounts] = useLazyListAccountsQuery();

  return [
    ...defaultSearchParams({
      count: 10,
      page: 0,
      sortBy: 'createdDate',
      sortDirection: 'DESC',
    }),
    querySearchParam('phone', { defaultValue: '' }),
    luxonDateSearchParam('startdate', {
      defaultValue: DateTime.local().startOf('day'),
      filter: {
        type: 'DateRangeFilter',
        label: t('forms.fields.dateRange.label'),
        startParamName: 'startdate',
        endParamName: 'enddate',
        predefinedDateOptions: predefinedRangeOptions([
          'this-quarter',
          'this-year',
          'last-quarter',
          'last-year',
        ]),
      },
    }),
    luxonDateSearchParam('enddate', {
      defaultValue: DateTime.local().endOf('day'),
    }),
    stringListSearchParam('type', {
      defaultValue: ['ALL'],
      options: typeOptions,
      filter: {
        type: 'MultiSelectFilter',
        label: t('page.transactions.filters.paymentTypeFilter.label'),
      },
    }),
    stringSearchParam('status', {
      defaultValue: 'ALL',
      options: statusOptions,
      filter: {
        type: 'SingleSelectFilter',
        label: t('page.transactions.filters.statusFilter.label'),
      },
    }),
    stringListSearchParam('paymentaccounts', {
      defaultValue: ['ALL'],
      options: [
        {
          label: t(
            'page.transactions.filters.paymentAccountFilter.selectAllOption',
          ),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const { data: accounts } = await triggerAccounts({
          count: 1000,
          page: 0,
        });

        return [
          {
            label: t(
              'page.transactions.filters.paymentAccountFilter.selectAllOption',
            ),
            id: 'ALL',
          },
        ].concat(
          accounts?.content.map(({ accountName, accountId }) => ({
            label: accountName,
            id: accountId,
          })) ?? [],
        );
      },
      filter: {
        type: 'MultiSelectFilter',
        label: t('page.transactions.filters.paymentAccountFilter.label'),
      },
    }),
  ];
};
